import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config/api';

function CommercialDashboard({ onNavigate }) {
  const { token } = useAuth();
  const [metrics, setMetrics] = useState({
    gmv_amount: 0,
    net_revenue_amount: 0,
    paid_orders_count: 0,
    pending_payments_count: 0,
    pending_approvals_count: 0,
    active_campaigns_count: 0,
    active_sponsored_facilities_count: 0,
    recent_orders: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/commercial/metrics`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setMetrics(data);
        setError(null);
      } else {
        setError('Failed to fetch commercial metrics');
      }
    } catch (e) {
      setError('Network error fetching commercial metrics');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const formatRupees = (paise) => {
    if (paise === undefined || paise === null) return '₹0';
    return `₹${(paise / 100).toLocaleString('en-IN')}`;
  };

  if (loading) {
    return <div className="card"><p>Loading Commercial Operations Dashboard...</p></div>;
  }

  return (
    <div>
      {error && (
        <div style={{ padding: '12px', background: '#FFEBEE', color: '#C62828', borderRadius: '6px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* KPI Grid */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div className="card stat-card">
          <h3>Gross Booked GMV</h3>
          <div className="value" style={{ color: '#2E7D32' }}>{formatRupees(metrics.gmv_amount)}</div>
        </div>
        <div className="card stat-card">
          <h3>Net Platform Revenue</h3>
          <div className="value" style={{ color: '#1565C0' }}>{formatRupees(metrics.net_revenue_amount)}</div>
        </div>
        <div className="card stat-card">
          <h3>Paid Orders</h3>
          <div className="value">{metrics.paid_orders_count}</div>
        </div>
        <div className="card stat-card">
          <h3>Pending Payments</h3>
          <div className="value" style={{ color: metrics.pending_payments_count > 0 ? '#E65100' : 'inherit' }}>
            {metrics.pending_payments_count}
          </div>
        </div>
        <div className="card stat-card">
          <h3>Pending Approvals</h3>
          <div className="value" style={{ color: metrics.pending_approvals_count > 0 ? '#C62828' : 'inherit' }}>
            {metrics.pending_approvals_count}
          </div>
        </div>
        <div className="card stat-card">
          <h3>Active Banners</h3>
          <div className="value" style={{ color: '#2E7D32' }}>{metrics.active_campaigns_count}</div>
        </div>
        <div className="card stat-card">
          <h3>Active Sponsored Facilities</h3>
          <div className="value" style={{ color: '#6A1B9A' }}>{metrics.active_sponsored_facilities_count}</div>
        </div>
      </div>

      {/* Action Navigation Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '28px' }}>
        <button 
          className="btn-primary" 
          style={{ padding: '16px', textAlign: 'center', fontSize: '15px' }}
          onClick={() => onNavigate && onNavigate('commercial-orders')}
        >
          ➕ Create Commercial Order
        </button>
        <button 
          className="btn-primary" 
          style={{ padding: '16px', textAlign: 'center', fontSize: '15px', background: '#6A1B9A' }}
          onClick={() => onNavigate && onNavigate('commercial-approvals')}
        >
          🛡️ Review Pending Approvals ({metrics.pending_approvals_count})
        </button>
        <button 
          className="btn-primary" 
          style={{ padding: '16px', textAlign: 'center', fontSize: '15px', background: '#0277BD' }}
          onClick={() => onNavigate && onNavigate('commercial-accounts')}
        >
          🏢 Manage Advertisers
        </button>
        <button 
          className="btn-primary" 
          style={{ padding: '16px', textAlign: 'center', fontSize: '15px', background: '#37474F' }}
          onClick={() => onNavigate && onNavigate('commercial-catalog')}
        >
          🏷️ Rate Cards & Packages
        </button>
      </div>

      {/* Recent Orders Table */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3>Recent Commercial Orders</h3>
          <button className="btn-outline" onClick={() => onNavigate && onNavigate('commercial-orders')}>
            View All Orders
          </button>
        </div>

        {metrics.recent_orders.length === 0 ? (
          <p style={{ padding: '16px', color: '#666' }}>No commercial orders created yet.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order Number</th>
                <th>Advertiser</th>
                <th>Total Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {metrics.recent_orders.map((o) => (
                <tr key={o.id}>
                  <td><strong>{o.order_number}</strong></td>
                  <td>{o.account_legal_name || 'Advertiser'}</td>
                  <td>{formatRupees(o.total_amount)}</td>
                  <td>
                    <span className={`badge-${
                      o.status === 'FULFILLED' ? 'success' :
                      o.status === 'PAID' ? 'success' :
                      o.status === 'PENDING_PAYMENT' ? 'danger' : 'info'
                    }`}>
                      {o.status}
                    </span>
                  </td>
                  <td>{new Date(o.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default CommercialDashboard;
